import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Download, Loader2, Pencil, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore, ROLES } from '../lib/auth';
import { attendanceService } from '../services/attendance';
import { useLiveAttendance } from '../hooks/attendance/useLiveAttendance';
import { usePayrollSummary } from '../hooks/attendance/usePayrollSummary';
import { useTimeEntryHistory } from '../hooks/attendance/useTimeEntryHistory';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import LiveAttendanceTable from '../components/attendance/LiveAttendanceTable';
import PayrollSummaryCards from '../components/attendance/PayrollSummaryCards';
import { PayrollDetailTable, PayrollGroupedTable } from '../components/attendance/PayrollTables';
import AttendanceStatusBadge from '../components/attendance/AttendanceStatusBadge';
import EditShiftDialog from '../components/attendance/EditShiftDialog';
import CreateShiftDialog from '../components/attendance/CreateShiftDialog';
import {
  formatUKDate,
  formatUKTime,
  getPayrollWeekStartISO,
  getPayrollWeekRangeLabel,
  normalizeToPayrollWeekStart,
  renderPaidHours,
} from '../utils/attendanceDateTime';
import { exportVenuePayrollCsv, exportVenuePayrollPdf } from '../utils/payrollExport';

const MANAGER_ROLES = [ROLES.SUPER_ADMIN, ROLES.OPS, ROLES.MANAGER];

export default function TimeAttendancePage() {
  const { user } = useAuthStore();
  const [venues, setVenues] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [bootLoading, setBootLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('live');
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [weekStart, setWeekStart] = useState(getPayrollWeekStartISO());
  const [reviewDateFrom, setReviewDateFrom] = useState(getPayrollWeekStartISO());
  const [reviewDateTo, setReviewDateTo] = useState(() => {
    const d = new Date(getPayrollWeekStartISO());
    d.setDate(d.getDate() + 6);
    return d.toISOString().slice(0, 10);
  });
  const [selectedStaffId, setSelectedStaffId] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [search, setSearch] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  useEffect(() => {
    const loadBaseData = async () => {
      setBootLoading(true);
      try {
        const [loadedVenues, mappedStaff] = await Promise.all([
          attendanceService.getAccessibleVenuesForUser(user),
          attendanceService.getStaffDirectory(),
        ]);

        setVenues(loadedVenues);
        setSelectedVenueId((current) => current || loadedVenues[0]?.id || '');
        setStaffOptions(mappedStaff);
      } catch (err) {
        toast.error(attendanceService.mapErrorMessage(err, 'Failed to load attendance filters'));
      } finally {
        setBootLoading(false);
      }
    };

    if (user?.id) {
      loadBaseData();
    }
  }, [user, user?.id, user?.role]);

  const liveFilters = useMemo(
    () => ({
      venueId: selectedVenueId || null,
      role: selectedRole === 'all' ? null : selectedRole,
      search: search || null,
    }),
    [search, selectedRole, selectedVenueId]
  );

  const payrollFilters = useMemo(
    () => ({
      weekStart,
      venueId: selectedVenueId || null,
      staffId: selectedStaffId === 'all' ? null : selectedStaffId,
      role: selectedRole === 'all' ? null : selectedRole,
      status: selectedStatus === 'all' ? null : selectedStatus,
    }),
    [selectedRole, selectedStaffId, selectedStatus, selectedVenueId, weekStart]
  );

  const reviewFilters = useMemo(
    () => ({
      weekStart,
      dateFrom: reviewDateFrom,
      dateTo: reviewDateTo && reviewDateTo >= reviewDateFrom ? reviewDateTo : reviewDateFrom,
      venueId: selectedVenueId || null,
      staffId: selectedStaffId === 'all' ? null : selectedStaffId,
      role: selectedRole === 'all' ? null : selectedRole,
      status: selectedStatus === 'all' ? null : selectedStatus,
      search: search || null,
    }),
    [reviewDateFrom, reviewDateTo, search, selectedRole, selectedStaffId, selectedStatus, selectedVenueId, weekStart]
  );

  const {
    rows: liveRows,
    loading: liveLoading,
    error: liveError,
    refresh: refreshLive,
  } = useLiveAttendance(liveFilters);

  const {
    timeEntries,
    summaryRows,
    calculatedTotals,
    loading: payrollLoading,
    error: payrollError,
    refresh: refreshPayroll,
  } = usePayrollSummary(payrollFilters);

  const {
    rows: reviewRows,
    loading: reviewLoading,
    error: reviewError,
    refresh: refreshReview,
    editTimeEntry,
    createTimeEntry,
  } = useTimeEntryHistory(reviewFilters);

  const selectedVenueName = useMemo(
    () => venues.find((venue) => venue.id === selectedVenueId)?.name || '',
    [selectedVenueId, venues]
  );

  if (!MANAGER_ROLES.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleEditSubmit = async (payload) => {
    setEditSubmitting(true);
    try {
      await editTimeEntry(payload);
      toast.success('Shift updated successfully');
      setEditOpen(false);
      setEditingShift(null);
      refreshPayroll();
    } catch (err) {
      toast.error(attendanceService.mapErrorMessage(err, 'Failed to update shift'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleCreateShift = async (payload) => {
    setCreateSubmitting(true);
    try {
      await createTimeEntry(payload);
      toast.success('Manual shift created');
      refreshPayroll();
    } catch (err) {
      toast.error(attendanceService.mapErrorMessage(err, 'Failed to create shift'));
    } finally {
      setCreateSubmitting(false);
    }
  };

  if (bootLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-attendance-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
          Time & Attendance
        </h1>
        <p className="text-[#A1A1AA] mt-1">Live tracking, weekly payroll review, and manager shift corrections.</p>
      </div>

      <div className="card-rtb">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <div>
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Venue</p>
            <Select value={selectedVenueId} onValueChange={setSelectedVenueId}>
              <SelectTrigger className="input-rtb">
                <SelectValue placeholder="Select venue" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
                {venues.map((venue) => (
                  <SelectItem key={venue.id} value={venue.id}>{venue.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Week Start</p>
            <Input
              type="date"
              value={weekStart}
              onChange={(e) => setWeekStart(normalizeToPayrollWeekStart(e.target.value))}
              className="input-rtb"
            />
          </div>

          <div>
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Staff</p>
            <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
              <SelectTrigger className="input-rtb"><SelectValue placeholder="All staff" /></SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
                <SelectItem value="all">All Staff</SelectItem>
                {staffOptions.map((staff) => (
                  <SelectItem key={staff.id} value={staff.id}>{staff.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Role</p>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="input-rtb"><SelectValue placeholder="All roles" /></SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="ops">Ops</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Search</p>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-rtb"
              placeholder="Search staff name"
            />
          </div>

          <div>
            <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Status</p>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="input-rtb"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent className="bg-[#1E1E1E] border-[#3F3F46] text-white">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#1E1E1E] border border-[#3F3F46]">
          <TabsTrigger value="live">Live Attendance</TabsTrigger>
          <TabsTrigger value="weekly">Weekly Hours</TabsTrigger>
          <TabsTrigger value="review">Shift Review / Edit</TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button variant="secondary" onClick={refreshLive}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Live
            </Button>
          </div>
          {liveError && <p className="text-[#FFB4AF] text-sm">{liveError}</p>}
          {liveLoading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-7 h-7 text-[#FF6600] animate-spin" /></div>
          ) : (
            <LiveAttendanceTable rows={liveRows} />
          )}
        </TabsContent>

        <TabsContent value="weekly" className="space-y-4 mt-4">
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                const result = exportVenuePayrollCsv({
                  rows: timeEntries,
                  venueName: selectedVenueName,
                  weekStart,
                });
                if (!result.ok) {
                  toast.error(result.reason);
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Venue Payroll CSV
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                const result = exportVenuePayrollPdf({
                  rows: timeEntries,
                  venueName: selectedVenueName,
                  weekStart,
                  weekLabel: getPayrollWeekRangeLabel(weekStart),
                  totals: calculatedTotals,
                });
                if (!result.ok) {
                  toast.error(result.reason);
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Venue Payroll PDF
            </Button>
          </div>

          <PayrollSummaryCards venueName={selectedVenueName} weekStart={weekStart} totals={calculatedTotals} />
          {payrollError && <p className="text-[#FFB4AF] text-sm">{payrollError}</p>}
          {payrollLoading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-7 h-7 text-[#FF6600] animate-spin" /></div>
          ) : (
            <>
              <PayrollGroupedTable rows={summaryRows} />
              <PayrollDetailTable rows={timeEntries} />
            </>
          )}
        </TabsContent>

        <TabsContent value="review" className="space-y-4 mt-4">
          <div className="card-rtb">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Review From</p>
                <Input
                  type="date"
                  value={reviewDateFrom}
                  onChange={(e) => setReviewDateFrom(e.target.value)}
                  className="input-rtb"
                />
              </div>
              <div>
                <p className="text-[#A1A1AA] text-xs mb-2 uppercase">Review To</p>
                <Input
                  type="date"
                  value={reviewDateTo}
                  min={reviewDateFrom}
                  onChange={(e) => setReviewDateTo(e.target.value)}
                  className="input-rtb"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-end">
            <CreateShiftDialog
              staffOptions={staffOptions}
              venueOptions={venues}
              onSubmit={handleCreateShift}
              submitting={createSubmitting}
            />
            <Button variant="secondary" onClick={refreshReview}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Review
            </Button>
          </div>

          {reviewError && <p className="text-[#FFB4AF] text-sm">{reviewError}</p>}
          {reviewLoading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-7 h-7 text-[#FF6600] animate-spin" /></div>
          ) : (
            <div className="card-rtb overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#3F3F46]">
                    <TableHead>Staff</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>In</TableHead>
                    <TableHead>Out</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Edit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewRows.map((row) => (
                    <TableRow key={row.id || row.time_entry_id} className="border-[#27272A]">
                      <TableCell className="text-white">{row.staff_name || '-'}</TableCell>
                      <TableCell className="text-[#E4E4E7]">{row.venue_name || '-'}</TableCell>
                      <TableCell className="text-[#E4E4E7]">{formatUKDate(row.shift_date || row.clock_in_at)}</TableCell>
                      <TableCell className="text-[#E4E4E7]">{formatUKTime(row.clock_in_at)}</TableCell>
                      <TableCell className="text-[#E4E4E7]">{row.clock_out_at ? formatUKTime(row.clock_out_at) : '-'}</TableCell>
                      <TableCell className="text-[#E4E4E7]">{renderPaidHours(row.paid_hours || 0)}</TableCell>
                      <TableCell><AttendanceStatusBadge status={row.status || (row.clock_out_at ? 'closed' : 'open')} /></TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setEditingShift(row);
                            setEditOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <EditShiftDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        shift={editingShift}
        onSubmit={handleEditSubmit}
        submitting={editSubmitting}
      />
    </div>
  );
}
